import { useState, useEffect, type MouseEvent } from "react";
import toast from "react-hot-toast";
import { OrderI } from "../utils/types";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { orderService } from "../services";
import { getOrderRestaurantRating, extractId } from "../utils/helpers";
import ItemsAddedModal from "../modal/ItemsAddedModal";
import RatingModal from "../modal/RatingModal";
import blackStar from "../assets/images/icons/blackstar.png";
import filledStar from "../assets/images/icons/star.png";
import bike from "../assets/images/order/bike.png";

const formatOrderDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const Order = ({
  order,
  selectedOrderIndex,
  index,
  tab,
  onRated,
}: {
  order: OrderI;
  selectedOrderIndex: number;
  index: number;
  tab: string;
  onRated?: (orderId: string, rating: number) => void;
}) => {
  const navigate = useNavigate();
  const { addToCart, clearCart, getCartRestaurantId } = useCart();
  const [rating, setRating] = useState<number>(() =>
    getOrderRestaurantRating(order)
  );
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [modalRating, setModalRating] = useState(0);
  const [reordering, setReordering] = useState(false);
  const [showItemsAdded, setShowItemsAdded] = useState(false);

  useEffect(() => {
    setRating(getOrderRestaurantRating(order));
  }, [order]);

  const locationLabel = [
    order.restaurant?.location?.city,
    order.restaurant?.location?.state,
  ]
    .filter(Boolean)
    .join(", ") ||
    order.restaurant?.location?.address ||
    "Address not available";

  const handleRate = async (value: number, e: MouseEvent) => {
    e.stopPropagation();
    if (submittingRating || rating > 0) return;
    setModalRating(value);
    setRatingModalOpen(true);
  };

  const handleSubmitRating = async (value: number, review: string) => {
    const restaurantId =
      extractId(order.restaurantId) || extractId(order.restaurant?._id);
    if (!restaurantId) {
      toast.error("Restaurant not found");
      throw new Error("Restaurant not found");
    }
    setSubmittingRating(true);
    try {
      await orderService.rateRestaurant({
        orderId: order._id,
        restaurantId,
        rating: value,
        review,
      });
      setRating(value);
      onRated?.(order._id, value);
      toast.success("Thanks for your rating!");
    } catch (error: any) {
      toast.error(error?.message || "Failed to submit rating");
      throw error;
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleReorder = async (e: MouseEvent) => {
    e.stopPropagation();
    if (!order.items?.length) {
      toast.error("No items to reorder");
      return;
    }
    setReordering(true);
    try {
      const restaurantId = order.restaurantId || order.restaurant?._id;
      if (!restaurantId) {
        toast.error("Restaurant not found for reorder");
        return;
      }
      const cartRestaurantId = getCartRestaurantId();
      if (cartRestaurantId && cartRestaurantId !== restaurantId) {
        await clearCart();
      }
      for (const item of order.items) {
        const dishId = item.dishId || (item as any).dish?._id;
        if (!dishId) continue;
        await addToCart(dishId, restaurantId, item.quantity || 1, {
          variantId: (item as any).variantId ?? null,
          addons: (item as any).addons || [],
        });
      }
      setShowItemsAdded(true);
    } catch (error: any) {
      toast.error(error?.message || "Failed to reorder");
    } finally {
      setReordering(false);
    }
  };

  if (tab === "previous") {
    return (
      <>
        <div
          className={`rounded-2xl border border-[#E8E8E8] dark:border-gray-700 bg-white dark:bg-gray-800 p-3.5 mt-4 transition-colors duration-300 ${
            selectedOrderIndex === index ? "border-[#7402A7] border-2" : ""
          }`}
        >
          {/* Kitchen header */}
          <div className="flex gap-3 items-center pb-3 border-b border-[#EFEFEF] dark:border-gray-700">
            <img
              src={
                order.restaurant?.kitchenPhotos?.[0] ||
                "https://via.placeholder.com/48"
              }
              alt={order.restaurant?.businessName || "restaurant"}
              className="h-12 w-12 rounded-xl object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="text-[#050505] dark:text-white font-urbanist text-[15px] font-semibold leading-tight truncate">
                {order.restaurant?.businessName || "Restaurant"}
              </p>
              <p className="text-[#8A8A8A] dark:text-gray-400 font-urbanist text-xs mt-0.5 truncate">
                {locationLabel}
              </p>
            </div>
          </div>

          {/* Items */}
          <div className="py-3 border-b border-dashed border-[#D0D0D0] dark:border-gray-600 space-y-1">
            {order.items?.length ? (
              order.items.map((item) => (
                <p
                  key={item._id}
                  className="text-[#050505] dark:text-white font-urbanist text-sm font-medium"
                >
                  {item.quantity} x {item.dishName}
                  {item.variantName ? ` (${item.variantName})` : ""}
                </p>
              ))
            ) : (
              <p className="text-[#8A8A8A] text-sm">No items</p>
            )}
          </div>

          {/* Date / status / price */}
          <div className="flex justify-between items-start py-3 border-b border-[#EFEFEF] dark:border-gray-700">
            <div>
              <p className="text-[#8A8A8A] dark:text-gray-400 font-urbanist text-xs">
                Order Placed on {formatOrderDate(order.createdAt)}
              </p>
              <p className="text-[#8A8A8A] dark:text-gray-500 font-urbanist text-xs mt-1 lowercase">
                {order.orderStatus}
              </p>
            </div>
            <p className="text-[#050505] dark:text-white font-urbanist text-base font-semibold">
              ₹{Number(order.totalAmount).toFixed(2)}
            </p>
          </div>

          {/* Rate + Re-Order */}
          <div
            className="flex justify-between items-center pt-3 gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-[#050505] dark:text-white font-urbanist text-xs font-medium whitespace-nowrap">
                {rating > 0 ? "You rated" : "Rate Now"}
              </span>
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, starIndex) => (
                  <button
                    key={starIndex}
                    type="button"
                    disabled={submittingRating || rating > 0}
                    onClick={(e) => handleRate(starIndex + 1, e)}
                    className="p-0.5 disabled:cursor-default"
                    aria-label={`Rate ${starIndex + 1} stars`}
                  >
                    <img
                      src={starIndex < rating ? filledStar : blackStar}
                      alt=""
                      className={`h-4 w-4 ${
                        starIndex < rating ? "" : "opacity-70"
                      } ${submittingRating ? "opacity-40" : ""}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={handleReorder}
              disabled={reordering}
              className="flex-shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#D0D0D0] dark:border-gray-600 bg-[#F7F7F7] dark:bg-gray-700 text-[#050505] dark:text-white font-urbanist text-xs font-semibold disabled:opacity-50"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M17 1l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 11V9a4 4 0 014-4h14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 23l-4-4 4-4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 13v2a4 4 0 01-4 4H3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {reordering ? "Adding..." : "Re-Order"}
            </button>
          </div>
        </div>

        {showItemsAdded && (
          <ItemsAddedModal
            onClose={() => setShowItemsAdded(false)}
            onViewCart={() => {
              setShowItemsAdded(false);
              navigate("/order-summary");
            }}
            onContinueShopping={() => {
              setShowItemsAdded(false);
              navigate("/");
            }}
          />
        )}
        {ratingModalOpen && (
          <RatingModal
            onClose={() => setRatingModalOpen(false)}
            orderId={order._id}
            type="restaurant"
            restaurantId={
              extractId(order.restaurantId) || extractId(order.restaurant?._id)
            }
            restaurantName={order.restaurant?.businessName}
            initialRating={modalRating}
            onSubmit={handleSubmitRating}
          />
        )}
      </>
    );
  }

  // Pending orders card
  return (
    <div
      className={`rounded-2xl border border-[#E8E8E8] dark:border-gray-700 bg-white dark:bg-gray-800 p-3.5 mt-4 transition-colors duration-300 ${
        selectedOrderIndex === index ? "border-2 border-[#7402A7]" : ""
      }`}
    >
      <div className="flex gap-3 items-center pb-3 border-b border-[#EFEFEF] dark:border-gray-700">
        <img
          src={
            order.restaurant?.kitchenPhotos?.[0] ||
            "https://via.placeholder.com/48"
          }
          alt={order.restaurant?.businessName || "restaurant"}
          className="h-12 w-12 rounded-xl object-cover flex-shrink-0"
        />
        <div className="min-w-0">
          <p className="text-[#050505] dark:text-white font-urbanist text-[15px] font-semibold leading-tight truncate">
            {order.restaurant?.businessName || "Restaurant"}
          </p>
          <p className="text-[#8A8A8A] dark:text-gray-400 font-urbanist text-xs mt-0.5 truncate">
            {locationLabel}
          </p>
        </div>
      </div>

      <div className="py-3 border-b border-dashed border-[#D0D0D0] dark:border-gray-600 space-y-1">
        {order.items?.length ? (
          order.items.map((item) => (
            <p
              key={item._id}
              className="text-[#050505] dark:text-white font-urbanist text-sm font-medium"
            >
              {item.quantity} x {item.dishName}
            </p>
          ))
        ) : (
          <p className="text-[#8A8A8A] text-sm">No items</p>
        )}
      </div>

      <div className="flex justify-between items-center py-3">
        <p className="text-[#8A8A8A] dark:text-gray-400 font-urbanist text-xs">
          Order Placed on {formatOrderDate(order.createdAt)}
        </p>
        <p className="text-[#050505] dark:text-white font-urbanist text-base font-semibold">
          ₹{Number(order.totalAmount).toFixed(2)}
        </p>
      </div>

      <div className="flex justify-center items-center border border-[#E8E8E8] dark:border-gray-600 py-3 px-2 rounded-lg gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
        <img src={bike} alt="" className="h-5 w-5" />
        <p className="text-[#7402A7] dark:text-purple-400 font-urbanist text-sm font-semibold">
          Track Your Order
        </p>
      </div>
    </div>
  );
};

export default Order;
